import type { HttpHeaders } from '@ps/web-core-api';
import { Brand, Country, FeatureFlag, Site } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type { Language, WebsiteConfig, WebsiteLanguages } from '@ps/web-martech-cms-types';
import { invert } from 'lodash';
import type { Selector } from 'reselect';

import { select } from '../../../index';
import type { RootState, SelectorCreator, Slicer } from '../../types';

import type { CMSState } from './cms';

type SiteSuffixMap = { [key in Site]: string };

const BrandCodes = {
  [Brand.PokerStars]: 'PS',
  [Brand.PokerStarsCasino]: 'SC',
  [Brand.BetStars]: 'BS',
  [Brand.FullTilt]: 'FT',
  [Brand.PokerStarsVegas]: 'VS',
  [Brand.FoxBet]: 'FXB'
};

const BrandDisplayNames: Partial<Record<Brand, string>> = {
  [Brand.PokerStarsCasino]: 'PokerStars Casino',
  [Brand.BetStars]: 'PokerStars Sports'
};

const selectors = (slice: Slicer<CMSState>, createSelector: SelectorCreator) => ({
  getAlternateLinks() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) =>
        (select.location.stripPrefix as Selector<RootState, string>)(rootState),
      (website, strippedPathname) =>
        website?.alternate_links
          ?.filter(({ link: { excluded_paths: excludedPaths } }) => {
            if (excludedPaths?.length) {
              const joinedExcludedPaths = excludedPaths
                .map((ep) => ep.path.replace(/(^\/)|(\/$)/g, ''))
                .join('|');
              const startWithExcludedPath = new RegExp(`^/(${joinedExcludedPaths})/.*`, 'g');
              return !startWithExcludedPath.test(strippedPathname);
            }

            return true;
          })
          // eslint-disable-next-line @typescript-eslint/naming-convention
          ?.map(({ link: { locale_code: localeCode, base_url: baseUrl, replaced_path_start } }) => {
            const linkPath =
              replaced_path_start?.path_start_to_replace && replaced_path_start?.new_path_start
                ? strippedPathname.replace(
                    new RegExp(`^${replaced_path_start.path_start_to_replace}`),
                    replaced_path_start.new_path_start
                  )
                : strippedPathname;

            return {
              localeCode,
              url: `${baseUrl}${linkPath}`
            };
          })
    );
  },
  getWebSiteBrandName() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (website) => website?.brand || 'Unknown'
    );
  },
  getWebsiteBrandId() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebSiteBrandName(rootState),
      (websiteBrand) => Brand[websiteBrand]
    );
  },
  getPathBrandId() {
    return createSelector(
      (rootState: RootState, pathname: string | undefined) => pathname,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.location.stripBaseURL(rootState, undefined),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (pathname, websiteBrandId, locationPathname, siteId) => {
        const path = pathname ?? locationPathname;
        if (
          [
            Brand.BetStars,
            Brand.PokerStars,
            Brand.PokerStarsCasino,
            Brand.PokerStarsVegas
          ].includes(websiteBrandId)
        ) {
          switch (true) {
            case path.startsWith('/poker/'):
              return Brand.PokerStars;
            case path.startsWith('/vegas/') && (siteId === Site.DE || siteId === Site.DESH):
              return Brand.PokerStarsCasino;
            case path.startsWith('/vegas/'):
              return Brand.PokerStarsVegas;
            case path.startsWith('/casino/'):
              return Brand.PokerStarsCasino;
            case path.startsWith('/sports/'):
              return Brand.BetStars;
          }
        }
        return websiteBrandId;
      }
    );
  },
  getBrandId() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      ({ pathBrand }, websiteBrandId) => pathBrand || websiteBrandId
    );
  },
  getPathBrandName() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      ({ pathBrand }) => Brand[pathBrand as Brand] || Brand[Brand.PokerStars]
    );
  },
  getBrandName(this: { getBrandId: Selector<RootState, Brand> }) {
    return createSelector(
      this.getBrandId,
      (brandId) => BrandDisplayNames[brandId] ?? invert(Brand)[brandId]
    );
  },
  getBrandCode() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brandId) => BrandCodes[brandId as keyof typeof BrandCodes]
    );
  },
  getCanonicalLink() {
    return slice((state) => state.website?.canonical_link);
  },
  getContent() {
    return slice(({ content }) => content);
  },
  getContentItem() {
    return createSelector(
      (rootState: RootState) => select.cms.getContent(rootState),
      (_rootState: RootState, uid: string) => uid,
      (content, uid) => content[uid]
    );
  },
  getDomain() {
    return createSelector(
      slice,
      (rootState: RootState) => select.location.getHostname(rootState),
      ({ website }, hostname) =>
        (website?.domains.find((domain) => hostname.startsWith(domain)) ||
          website?.domains[0]) as string
    );
  },
  getLegalAge() {
    return slice(({ website }) => website?.legal_age);
  },
  getNotificationSubscriptionPath() {
    return createSelector(
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.device.getId(rootState),
      (rootState: RootState) => select.account.getLicense(rootState),
      (rootState: RootState) => select.account.getUserId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (locale, device, license, userId, site) =>
        `/user/queue/starsweb/notifications/${locale}/${device}/${license}/${site}/${userId}`
    );
  },
  getFaviconURL() {
    return slice((state) => state.website?.favicon?.url);
  },
  getRegion() {
    return slice((state) => state.website?.region || undefined);
  },
  /**
   * @deprecated For backported API compatibility only. Use `bindRequest` instead.
   */
  getRequestOptions() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.cms.getRegion(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => select.device.getPlatform(rootState),
      (rootState: RootState) => select.device.get(rootState),
      (rootState: RootState) => select.currency.get(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.location.getHref(rootState),
      (rootState: RootState) => select.location.getOrigin(rootState),
      (rootState: RootState) => select.location.getHost(rootState),
      (rootState: RootState) => select.system.get(rootState),
      (
        website,
        websiteBrand,
        site,
        region,
        country,
        platform,
        { device, os, embedded },
        currency,
        locale,
        session,
        href,
        origin,
        host,
        systemState
      ) => {
        const requestMeta = { locale, currency, systemState };
        const headers: HttpHeaders = {
          referer: href,
          ['x-fi-ps-platform-id']: `${platform}`
        };

        if (!canUseDOM) {
          if (website) Object.assign(requestMeta, { site, brand: websiteBrand });

          Object.assign(requestMeta, {
            device,
            country,
            os,
            embedded: embedded ? 1 : 0
          });
          headers['x-forwarded-host'] = host;
          headers.origin = origin;
          if (region) (requestMeta as typeof requestMeta & { region: string }).region = region;
        }

        if (session.token) headers.authorization = `StarsWeb-Session ${session.token}`;
        return { meta: requestMeta, headers };
      }
    );
  },
  getAliases() {
    return slice((state) => state.aliases || {});
  },
  getSiteId() {
    return slice(
      (state) =>
        Site[(state.website?.site as keyof typeof Site) || ('COM' as keyof typeof Site)] as Site
    );
  },
  getSiteName() {
    return slice((state) => state.website?.site || 'COM');
  },
  getStarsDomain() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (id: number) =>
        ({
          [Site.IT]: 'starsaccount.it',
          [Site.GR]: 'starsaccount.gr',
          [Site.FR]: 'starsaccount.fr',
          [Site.ES]: 'starsaccount.es',
          [Site.DK]: 'starsaccount.dk',
          [Site.EU]: 'starsaccount.eu',
          [Site.UK]: 'starsaccount.uk',
          [Site.BG]: 'starsaccount.bg',
          [Site.DESH]: 'sh.starsaccount.de',
          [Site.RO]: 'starsaccount.ro',
          [Site.EE]: 'starsaccount.ee',
          [Site.CZ]: 'starsaccount.cz',
          [Site.BE]: 'starsaccount.be',
          [Site.RUSOPM]: 'starsaccount.net',
          [Site.PT]: 'starsaccount.pt',
          [Site.SE]: 'starsaccount.se',
          [Site.NET]: 'starsaccount.net',
          [Site.USPA]: 'starsaccountpa.com',
          [Site.USNJ]: 'pokerstarscasinonj.com',
          [Site.USMI]: 'pokerstarscasinomi.com',
          [Site.NL]: 'pokerstars.nl'
        }[id] || 'starsaccount.com')
    );
  },
  getToken() {
    return createSelector(
      slice,
      (_rootState: RootState, token: string) => token,
      ({ website }, token) => website?.tokens.find(({ key }) => key === token)?.value
    );
  },
  getCashierHref() {
    const siteSuffixMap: SiteSuffixMap = {
      [Site.COM]: '',
      [Site.NET]: '',
      [Site.IT]: 'it',
      [Site.FR]: '',
      [Site.EE]: 'ee',
      [Site.BE]: 'be',
      [Site.ES]: 'es',
      [Site.DK]: 'dk',
      [Site.EU]: 'eu',
      [Site.USNJ]: 'usnj',
      [Site.UK]: 'uk',
      [Site.BG]: 'bg',
      [Site.DESH]: '',
      [Site.RO]: 'ro',
      [Site.PT]: 'pt',
      [Site.CZ]: 'cz',
      [Site.RUSO]: '',
      [Site.RUSOPM]: '',
      [Site.IN]: '',
      [Site.ASIA]: '',
      [Site.RU]: '',
      [Site.USPA]: '',
      [Site.SE]: 'se',
      [Site.CH]: '',
      [Site.ARBA]: '',
      [Site.USCO]: 'uspa',
      [Site.DE]: '',
      [Site.USMI]: 'uspa',
      [Site.NL]: '',
      [Site.CAON]: '',
      [Site.GR]: ''
    };

    return createSelector(
      (rootState: RootState) => select.device.isEmbedded(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (isEmbedded, site) =>
        isEmbedded ? `pokerstars${siteSuffixMap[site]}://cashier/open` : undefined
    );
  },
  getWebsite() {
    return slice(({ website }) => website);
  },
  getWebsiteLanguages() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (website, content) => {
        const refs = website?.website_languages;
        const websiteLanguages = !!refs?.length && (content[refs[0].uid] as WebsiteLanguages);
        if (!websiteLanguages) return [];
        return websiteLanguages.languages.map((item) => (content[item.uid] as Language).short_code);
      }
    );
  },
  getWebsiteLaunchDate() {
    return slice(({ website, content }) => {
      const refs = website?.website_config;
      const websiteConfig = !!refs?.length && (content[refs[0].uid] as WebsiteConfig);
      return websiteConfig ? websiteConfig.isp_launch_date : undefined;
    });
  },
  getTouchpointConfig() {
    return slice(({ website, content }) => {
      const refs = website?.website_config;
      const websiteConfig = !!refs?.length && (content[refs[0].uid] as WebsiteConfig);
      return websiteConfig ? websiteConfig.touchpoint : undefined;
    });
  },
  isAuthRequired() {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (pathname, website) => website?.auth_required_paths.includes(pathname)
    );
  },
  isSiteUS() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => [Site.USMI, Site.USNJ, Site.USPA, Site.USCO].includes(site)
    );
  },
  isIndefiniteExclusionAllowed(this: {
    getSiteId: Selector<RootState, Site>;
    showVegasContent: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getSiteId,
      this.showVegasContent,
      (site, showVegasContent) => [Site.COM, Site.EU, Site.NL].includes(site) && !showVegasContent
    );
  },
  showVegasContent(this: { getCountry: Selector<RootState, string> }) {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => rootState.account?.country,
      (site, geoCountry, accountCountry) =>
        site === Site.DESH ||
        site === Site.DE ||
        (site === Site.EU && (accountCountry || geoCountry) === Country.Germany)
    );
  },
  showOntarioContent(this: { getSiteId: Selector<RootState, Site> }) {
    return createSelector(this.getSiteId, (site) => site === Site.CAON);
  },
  getRedirectUrl() {
    return createSelector(
      (rootState: RootState) => select.localSettings.get(rootState),
      ({ redirectTo }) => redirectTo || '/'
    );
  },
  isIspRouting: () =>
    createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPRouting),
      (rootState: RootState) => select.location.getQuery(rootState),
      (ispRouting, { siteId }) => ispRouting || siteId === 'isp'
    )
});

export default selectors;
